import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardBody, Spinner, Button } from '@patternfly/react-core';
import { ToggleBtn } from '@app/components/ToggleBtn';
import { useModuleTimeSeries, useModuleEventBreakdown, useModuleConfig, useEventPayloadBreakdown } from '../hooks/useUsageAnalytics';
import { Granularity, ChartType, GRANULARITY_OPTIONS, DAYS_OPTIONS, ModuleTimeSeriesPoint, ModuleEventBreakdownPoint, PayloadBreakdownPoint } from '../types';

const FALLBACK_COLORS = ['#60a5fa', '#fca5a5', '#34d399', '#fbbf24', '#c084fc', '#6b7280'];

function makeColorMap(modules: { module_name: string; color: string }[]): Record<string, string> {
  return Object.fromEntries(modules.map((m) => [m.module_name, m.color]));
}

function seriesColor(name: string, colorMap: Record<string, string>, index: number): string {
  return colorMap[name] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function formatPeriod(iso: string, granularity: Granularity): string {
  const d = new Date(iso);
  if (granularity === 'monthly') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (granularity === 'weekly') return `W/c ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function exportModuleCsv(data: ModuleTimeSeriesPoint[], granularity: Granularity) {
  const header = 'Period,Module,Calls\n';
  const body = data
    .map((r) => `${formatPeriod(r.period, granularity)},${r.module},${r.count}`)
    .join('\n');
  triggerDownload(header + body, `module-calls-${new Date().toISOString().slice(0, 10)}.csv`);
}

function exportBreakdownCsv(data: ModuleEventBreakdownPoint[], module: string, granularity: Granularity) {
  const header = 'Period,Event,Calls\n';
  const body = data
    .filter((r) => r.module === module)
    .map((r) => `${formatPeriod(r.period, granularity)},${r.event_name},${r.count}`)
    .join('\n');
  triggerDownload(header + body, `${module.replace(/\s+/g, '-').toLowerCase()}-events-${new Date().toISOString().slice(0, 10)}.csv`);
}

function triggerDownload(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Pivot breakdown data for a single module: period → event_name → count */
function pivotBreakdown(
  raw: ModuleEventBreakdownPoint[],
  module: string,
  granularity: Granularity,
): { period: string; periodIso: string; [event: string]: number | string }[] {
  const byPeriod = new Map<string, { periodIso: string; counts: Record<string, number> }>();
  for (const row of raw) {
    if (row.module !== module) continue;
    const label = formatPeriod(row.period, granularity);
    if (!byPeriod.has(label)) byPeriod.set(label, { periodIso: row.period, counts: {} });
    byPeriod.get(label)!.counts[row.event_name] =
      (byPeriod.get(label)!.counts[row.event_name] ?? 0) + row.count;
  }
  return Array.from(byPeriod.entries())
    .map(([period, { periodIso, counts }]) => ({ period, periodIso, ...counts }))
    .sort((a, b) => (a.periodIso < b.periodIso ? -1 : a.periodIso > b.periodIso ? 1 : 0));
}

function pivotData(
  raw: ModuleTimeSeriesPoint[],
  granularity: Granularity,
): { period: string; periodIso: string; [module: string]: number | string }[] {
  const byPeriod = new Map<string, { periodIso: string; counts: Record<string, number> }>();
  for (const row of raw) {
    const label = formatPeriod(row.period, granularity);
    if (!byPeriod.has(label)) byPeriod.set(label, { periodIso: row.period, counts: {} });
    byPeriod.get(label)!.counts[row.module] =
      (byPeriod.get(label)!.counts[row.module] ?? 0) + row.count;
  }
  return Array.from(byPeriod.entries())
    .map(([period, { periodIso, counts }]) => ({ period, periodIso, ...counts }))
    .sort((a, b) => (a.periodIso < b.periodIso ? -1 : a.periodIso > b.periodIso ? 1 : 0));
}

/**
 * Build a lookup: periodIso -> module -> event_name -> count
 * Used by the custom tooltip to render per-event breakdown.
 */
function buildBreakdownLookup(
  breakdown: ModuleEventBreakdownPoint[],
): Record<string, Record<string, Record<string, number>>> {
  const lookup: Record<string, Record<string, Record<string, number>>> = {};
  for (const row of breakdown) {
    if (!lookup[row.period]) lookup[row.period] = {};
    if (!lookup[row.period][row.module]) lookup[row.period][row.module] = {};
    lookup[row.period][row.module][row.event_name] = row.count;
  }
  return lookup;
}

interface CustomTooltipProps {
  active?: boolean;
  label?: string;
  payload?: { name: string; value: number; fill: string }[];
  periodIsoByLabel: Record<string, string>;
  breakdownLookup: Record<string, Record<string, Record<string, number>>>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active, label, payload, periodIsoByLabel, breakdownLookup,
}) => {
  if (!active || !payload?.length || !label) return null;

  const periodIso = periodIsoByLabel[label];
  const modulesInPeriod = breakdownLookup[periodIso] ?? {};

  return (
    <div className="ua-tooltip">
      <div className="ua-tooltip__title">{label}</div>
      {payload.map((entry) => {
        const events = modulesInPeriod[entry.name] ?? {};
        const sortedEvents = Object.entries(events).sort(([, a], [, b]) => b - a);
        return (
          <div key={entry.name} className="ua-tooltip__module">
            <div className="ua-tooltip__module-header">
              <span className="ua-tooltip__color-swatch" style={{ background: entry.fill }} />
              <span className="ua-tooltip__module-name">{entry.name}</span>
              <span className="ua-tooltip__module-total">{entry.value.toLocaleString()}</span>
            </div>
            {sortedEvents.length > 0 && (
              <div className="ua-tooltip__events">
                {sortedEvents.map(([eventName, count]) => (
                  <div key={eventName} className="ua-tooltip__event-row">
                    <span className="ua-tooltip__event-name">{eventName}</span>
                    <span className="ua-tooltip__event-count">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


interface PayloadBreakdownSectionProps {
  eventName: string;
  payloadField: string;
  days: number;
  cluster?: string | null;
}

const PayloadBreakdownSection: React.FC<PayloadBreakdownSectionProps> = ({
  eventName, payloadField, days, cluster,
}) => {
  const { data, isLoading } = useEventPayloadBreakdown(eventName, payloadField, days, cluster);
  if (isLoading) return <Spinner size="sm" />;
  if (data.length === 0) return null;

  const total = data.reduce((s, r) => s + r.count, 0);
  const label = payloadField === 'serviceName' ? 'By Service' : payloadField === 'target' ? 'By Config Target' : payloadField;

  return (
    <div className="ua-payload-breakdown__section">
      <div className="ua-payload-breakdown__field-label">{label}</div>
      {data.map((row) => {
        const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
        return (
          <div key={row.field_value} className="ua-payload-breakdown__row">
            <span className="ua-payload-breakdown__value">{row.field_value}</span>
            <div className="ua-payload-breakdown__bar-wrap">
              <div className="ua-payload-breakdown__bar" style={{ width: `${pct}%` }} />
            </div>
            <span className="ua-payload-breakdown__count">{row.count.toLocaleString()}</span>
          </div>
        );
      })}
    </div>
  );
};

export const ModuleTimeSeriesChart: React.FC<{ cluster?: string | null }> = ({ cluster }) => {
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [days, setDays] = useState<number>(7);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  const { data, isLoading, isFetching, error } = useModuleTimeSeries(granularity, days, cluster);
  const { data: breakdownData } = useModuleEventBreakdown(granularity, days, cluster);
  const { modules: moduleConfig } = useModuleConfig();

  const colorMap = useMemo(() => makeColorMap(moduleConfig), [moduleConfig]);

  const selectedModuleConfig = useMemo(
    () => moduleConfig.find((m) => m.module_name === selectedModule) ?? null,
    [moduleConfig, selectedModule],
  );
  const payloadBreakdownFields = selectedModuleConfig?.payload_breakdown_fields ?? [];

  const modules = useMemo(
    () => Array.from(new Set(data.map((d) => d.module))).sort(),
    [data],
  );

  const chartData = useMemo(() => pivotData(data, granularity), [data, granularity]);

  // Drill-down: event-level series for a selected module
  const drillDownData = useMemo(
    () => (selectedModule ? pivotBreakdown(breakdownData, selectedModule, granularity) : []),
    [breakdownData, selectedModule, granularity],
  );
  const drillDownSeries = useMemo(
    () => selectedModule
      ? Array.from(new Set(
          breakdownData.filter((r) => r.module === selectedModule).map((r) => r.event_name),
        )).sort()
      : [],
    [breakdownData, selectedModule],
  );

  const breakdownLookup = useMemo(() => buildBreakdownLookup(breakdownData), [breakdownData]);

  const periodIsoByLabel = useMemo(
    () => Object.fromEntries(chartData.map((r) => [r.period, r.periodIso as string])),
    [chartData],
  );

  const activeChartData = selectedModule ? drillDownData : chartData;
  const isEmpty = activeChartData.length === 0;

  const csvAction = !isEmpty && (
    <Button
      variant="link"
      isInline
      onClick={() =>
        selectedModule
          ? exportBreakdownCsv(breakdownData, selectedModule, granularity)
          : exportModuleCsv(data, granularity)
      }
    >
      ↓ CSV
    </Button>
  );

  const sharedElements = (
    <>
      <CartesianGrid strokeDasharray="none" stroke="#f3f4f6" vertical={false} />
      <XAxis dataKey="period" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} tickMargin={8} />
      <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} tickMargin={8} allowDecimals={false} />
      <Tooltip
        content={
          selectedModule
            ? undefined  // default recharts tooltip for drill-down
            : (
              <CustomTooltip
                periodIsoByLabel={periodIsoByLabel}
                breakdownLookup={breakdownLookup}
              />
            )
        }
      />
      <Legend wrapperStyle={{ paddingTop: 16, fontSize: 12 }} iconType="rect" />
    </>
  );

  return (
    <Card>
      <CardHeader actions={{ actions: csvAction }}>
        <CardTitle>
          Calls by Module
          {selectedModule && (
            <span style={{ fontWeight: 400, fontSize: 14, marginLeft: 10, color: '#6b7280' }}>
              / {selectedModule}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <div className="ua-controls">
          <div className="ua-btn-group">
            {GRANULARITY_OPTIONS.map((o) => (
              <ToggleBtn key={o.value} label={o.label} active={granularity === o.value} onClick={() => setGranularity(o.value)} />
            ))}
          </div>
          <div className="ua-btn-group">
            {DAYS_OPTIONS.map((o) => (
              <ToggleBtn key={o.value} label={o.label} active={days === o.value} onClick={() => setDays(o.value)} />
            ))}
          </div>
          <div className="ua-btn-group">
            <ToggleBtn label="Bar" active={chartType === 'bar'} onClick={() => setChartType('bar')} />
            <ToggleBtn label="Line" active={chartType === 'line'} onClick={() => setChartType('line')} />
          </div>
          {isFetching && <Spinner size="sm" />}
        </div>

        {/* Module filter row */}
        {modules.length > 0 && (
          <div className="ua-controls" style={{ marginTop: 8 }}>
            <div className="ua-btn-group">
              <ToggleBtn label="All" active={selectedModule === null} onClick={() => setSelectedModule(null)} />
              {modules.map((mod, i) => (
                <ToggleBtn
                  key={mod}
                  label={mod}
                  active={selectedModule === mod}
                  onClick={() => setSelectedModule(selectedModule === mod ? null : mod)}
                />
              ))}
            </div>
          </div>
        )}

        {error && <div className="ua-error">Error: {error}</div>}

        {isLoading ? (
          <div className="ua-empty-state"><Spinner /></div>
        ) : isEmpty ? (
          <div className="ua-empty-state">
            {selectedModule
              ? `No event data for "${selectedModule}" in the selected period.`
              : 'No module activity data for the selected period.'}
          </div>
        ) : (
          <div style={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'bar' ? (
                <BarChart data={activeChartData} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
                  {sharedElements}
                  {selectedModule
                    ? drillDownSeries.map((evt, i) => (
                        <Bar key={evt} dataKey={evt} fill={FALLBACK_COLORS[i % FALLBACK_COLORS.length]} radius={[3, 3, 0, 0]} />
                      ))
                    : modules.map((mod, i) => (
                        <Bar key={mod} dataKey={mod} fill={seriesColor(mod, colorMap, i)} radius={[3, 3, 0, 0]} />
                      ))
                  }
                </BarChart>
              ) : (
                <LineChart data={activeChartData} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
                  {sharedElements}
                  {selectedModule
                    ? drillDownSeries.map((evt, i) => (
                        <Line key={evt} type="monotone" dataKey={evt} stroke={FALLBACK_COLORS[i % FALLBACK_COLORS.length]} strokeWidth={2} dot={false} />
                      ))
                    : modules.map((mod, i) => (
                        <Line key={mod} type="monotone" dataKey={mod} stroke={seriesColor(mod, colorMap, i)} strokeWidth={2} dot={false} />
                      ))
                  }
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}

        {/* Payload breakdown panel — shown when the selected module has configured payload fields */}
        {selectedModule && drillDownSeries.length > 0 && payloadBreakdownFields.length > 0 && (
          <div className="ua-payload-breakdown">
            <div className="ua-payload-breakdown__title">
              Breakdown for {drillDownSeries[0]}
            </div>
            <div className="ua-payload-breakdown__grid">
              {payloadBreakdownFields.map((field) => (
                <PayloadBreakdownSection
                  key={field}
                  eventName={drillDownSeries[0]}
                  payloadField={field}
                  days={days}
                  cluster={cluster}
                />
              ))}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
